/**
 * The ONE writer of a progression rule — what a rule write does to the map AND to the rows under it.
 *
 * Pure: it computes the slice of an exercise the write moves, and `applyExerciseAuthoring` applies
 * it. That split is what lets a surface ASK what a write would cost before dispatching it, and it is
 * why the invariants live here rather than in whichever control happens to be on screen.
 *
 * ── The three invariants this function is the only place to enforce ──────────────────────────
 * · **THE MEASUREMENT CONTRACT (R9) AND ITS MIRROR.** A rule keyed on a column the movement does not
 *   measure is refused, and so is one on a column another rule already BANDS — the band would return
 *   that column to its floor while a second rule advanced it, which the backend rejects outright.
 * · **THE REPLACED RULE.** The other direction of that same conflict is not a refusal but an ACT:
 *   putting a band on a column that carries a rule of its own takes that rule off, here, in the same
 *   patch. Doing it anywhere else would leave the two able to disagree, and would let the illegal
 *   shape exist for one render between two dispatches.
 * · **THE ORPHANED CEILING.** A ceiling names no column of its own; the `banded` field on the rule
 *   names it. So a column that STOPS carrying such a rule — or whose band moves elsewhere, or off
 *   entirely — leaves every authored ceiling naming nothing, and the rows are cleared in the same
 *   act. Without it the plan says "climb to 12" with nothing left that reads a 12.
 *
 * Taking a rule OFF is never refused. The backend normalises an unauthored document to a flat rule
 * on weight, so an exercise can arrive carrying a key on a column it cannot measure — and clearing
 * that is exactly the repair a trainer should be able to make. It still clears the band.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/progressionRulePatches.ts` ─────
 * `ruleOnColumnPatch` and `ruleDisplacedByBand` are 1:1. Mobile's `progressionRowDescriptor` is not
 * here: it words a chip from `formatRuleDelta`, and this app's card foot reads the map's kinds
 * directly (`features/plan-progression/model/ruleStatement.ts`). Two readings of the same map would
 * be two places for them to disagree about what an exercise says.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { MeasurementFieldKey } from '~/shared/measurement';

import type { ExerciseDraft, SetDraft } from './planDraft.types';
import { clearBand } from './progressionBand';
import { columnRuleRefusal, type ColumnRuleRefusal } from './progressionRefusals';
import { ruleFor, withRule, type ProgressionRule, type ProgressionRules } from './progressionRules';

/** What a rule write needs: the map it edits, the contract saying which columns may carry one, the rows. */
type RuleWriteSource = Pick<ExerciseDraft, 'progressionRules' | 'measurementFields' | 'sets'>;

/** The slice of an exercise a rule write moves. Empty is a refusal — nothing to say, nothing to apply. */
export type ColumnRulePatch = Partial<Pick<ExerciseDraft, 'progressionRules' | 'sets'>>;

/** What one rule write DID: the change to apply, and the reason there is none. */
export interface ColumnRuleWrite {
  readonly patch: ColumnRulePatch;
  /** Why the write was refused, or null when it landed. */
  readonly refusal: ColumnRuleRefusal | null;
}

/**
 * Put a rule on one column (or take it off), leaving every other column alone — and leaving the
 * exercise's ROWS consistent with what the rule map now says.
 */
export function ruleOnColumnPatch(
  exercise: RuleWriteSource,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
): ColumnRuleWrite {
  if (rule !== null) {
    const refusal = columnRuleRefusal(exercise, field);
    if (refusal !== null) return { patch: {}, refusal };
  }
  const progressionRules = rulesAfter(exercise, field, rule);
  const sets = rowsAfter(exercise, field, rule);
  return {
    patch: sets === null ? { progressionRules } : { progressionRules, sets },
    refusal: null,
  };
}

/**
 * The column whose rule a band write would DESTROY, or null when it destroys nothing.
 *
 * ONE PREDICATE, TWO CALLERS, and they must not drift: the sheet asks it before dispatching, to
 * decide whether the trainer has to be asked first, and the writer asks it while applying, to take
 * the displaced rule off in the same act. Two copies of "does this cost anything" is how a confirm
 * ends up guarding a write that no longer matches it — or how a write lands with no confirm at all.
 */
export function ruleDisplacedByBand(
  rules: ProgressionRules,
  steppedField: MeasurementFieldKey,
  bandedField: MeasurementFieldKey,
): MeasurementFieldKey | null {
  if (bandedField === steppedField) return null;
  return ruleFor(rules, bandedField) === null ? null : bandedField;
}

/** The rule map the write leaves behind — this column's rule, and the one a new band displaces. */
function rulesAfter(
  exercise: RuleWriteSource,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
): ProgressionRules {
  const written = withRule(exercise.progressionRules, field, rule);
  // A band and a rule may not share a column, so writing the band is what takes the other rule off —
  // through the same predicate the sheet's confirm is raised by.
  const banded = rule !== null && rule.type === 'PreviousExecution' ? rule.banded : null;
  const displaced =
    banded === null ? null : ruleDisplacedByBand(exercise.progressionRules, field, banded);
  return displaced === null ? written : withRule(written, displaced, null);
}

/** The rows the write leaves behind, or null where it does not touch them. See the orphaned ceiling. */
function rowsAfter(
  exercise: RuleWriteSource,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
): SetDraft[] | null {
  const outgoing = ruleFor(exercise.progressionRules, field);
  if (outgoing === null || outgoing.type !== 'PreviousExecution') return null;
  // A rule re-authored over the SAME banded column keeps its bars: the trainer changed the step, not
  // what the client climbs. Losing the band — to another column, or to a rule that has none —
  // orphans them.
  //
  // THE NON-NULL GUARD IS LOAD-BEARING. Both sides are nullable, so `null === null` would read two
  // rules that each place no bar as "same band, keep the ceilings" — leaving a ceiling standing
  // under a rule that names no column to read it in.
  if (
    rule !== null &&
    rule.type === 'PreviousExecution' &&
    rule.banded !== null &&
    rule.banded === outgoing.banded
  ) {
    return null;
  }
  return clearBand(exercise.sets);
}
